import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { PAGE_ROUTE_PATH, URL_PARAMS_KEY } from '@/constants';
import { useInternalRedirect } from '@/features/redirect';
import { useLoginUserInfo } from '@/hooks/useLoginUserInfo';

/**
 * ライセンス不正ダイアログのプロパティ
 */
type LicenseInvalidDialogContents = {
  // ダイアログタイトル
  title: string;
  // ダイアログメッセージ
  message: string;
  // GTMクラスタグ
  gtmClassTag: string;
};

/**
 * ライセンス不正 カスタムフック
 */
export const useLicenseInvalid = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSerialPlan } = useLoginUserInfo();
  const { internalRedirectToWelcome } = useInternalRedirect();

  const dialogTitle = useMemo(
    () => t('licenseInvalidDialog.このライセンスはご利用できません').toString(),
    [t],
  );

  /**
   * ダイアログタイトル、ダイアログメッセージ、GTMクラスタグをセットで返す
   */
  const licenseInvalidDialogContents =
    useMemo((): LicenseInvalidDialogContents => {
      if (isSerialPlan) {
        return {
          title: dialogTitle,
          message: t(
            'licenseInvalidDialog.ログアウトして再度お試しください。',
          ).toString(),
          gtmClassTag: 'dts-error-licence-serial',
        };
      }

      return {
        title: dialogTitle,
        message: t(
          'licenseInvalidDialog.ライセンスを再購入すると引き続きご利用いただけます。',
        ).toString(),
        gtmClassTag: 'dts-error-licence-ptid',
      };
    }, [dialogTitle, isSerialPlan, t]);

  /**
   * ライセンス不正ダイアログ用OKボタンのクリック処理
   */
  const clickLicenseInvalidOKButton = useCallback(() => {
    // シリアルで利用中
    if (isSerialPlan) {
      if (pathname === PAGE_ROUTE_PATH.HOME) {
        // ホーム画面を表示中の場合はリロード
        navigate(0);

        return;
      }
      // ホーム画面に遷移
      navigate({
        pathname: PAGE_ROUTE_PATH.HOME,
      });

      return;
    }

    // ようこそ画面に遷移。このときホーム画面のパスを指定
    internalRedirectToWelcome(
      encodeURI(`${URL_PARAMS_KEY.WELCOME.path}=${PAGE_ROUTE_PATH.HOME}`),
    );
  }, [internalRedirectToWelcome, isSerialPlan, navigate, pathname]);

  return {
    clickLicenseInvalidOKButton,
    licenseInvalidDialogContents,
  };
};
